import React, { useState, useEffect } from 'react';
import { Send, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { formatDate } from './RecordsTable';
import api, { formatApiErrorDetail } from '../../lib/api';
import { toast } from 'sonner';

const INVITE_TEXT = (row) =>
  `Hi ${row.name.split(' ')[0]}! You expressed interest in the Women's Club of Lincoln County — fall recruitment is open and we'd love for you to be one of our 25 founding members. Create your account here: ${window.location.origin}/register?name=${encodeURIComponent(row.name)}&email=${encodeURIComponent(row.email)}`;

export const InterestAdmin = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get('/admin/founding-interest')
      .then((res) => setRows(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleInvite = async (row) => {
    try {
      await api.post(`/admin/founding-interest/${row.id}/invite`);
      try {
        await navigator.clipboard.writeText(INVITE_TEXT(row));
        toast.success(`Invite for ${row.name} copied — paste it into an email or text!`);
      } catch {
        toast.success(`${row.name} marked as invited`);
      }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) return <p className="text-[#5f6d51]/60 text-sm py-8 text-center">Loading…</p>;
  if (rows.length === 0)
    return (
      <p className="text-[#5f6d51]/60 text-sm py-8 text-center" data-testid="admin-founding-interest-empty">
        No founding member interest submissions yet.
      </p>
    );

  return (
    <div className="overflow-x-auto rounded-lg border border-[#5f6d51]/10" data-testid="admin-founding-interest-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#f1e3eb] text-left">
            <th className="px-4 py-3 font-medium text-[#3c4a2a]">Date</th>
            <th className="px-4 py-3 font-medium text-[#3c4a2a]">Name</th>
            <th className="px-4 py-3 font-medium text-[#3c4a2a]">Email</th>
            <th className="px-4 py-3 font-medium text-[#3c4a2a]">Note</th>
            <th className="px-4 py-3 font-medium text-[#3c4a2a]">Invite</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-t border-[#5f6d51]/10 hover:bg-[#f1e3eb]/30">
              <td className="px-4 py-3 text-[#5f6d51] whitespace-nowrap">{formatDate(row.created_at)}</td>
              <td className="px-4 py-3 text-[#5f6d51]">{row.name}</td>
              <td className="px-4 py-3 text-[#5f6d51]">{row.email}</td>
              <td className="px-4 py-3 text-[#5f6d51] max-w-xs">{row.message || '—'}</td>
              <td className="px-4 py-3">
                {row.invited ? (
                  <div className="flex items-center gap-2">
                    <Badge className="bg-[#5d6b3c]/15 text-[#5d6b3c]">
                      <Check className="w-3 h-3 mr-1" />
                      Invited
                    </Badge>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => handleInvite(row)}
                      data-testid={`interest-reinvite-${row.id}`}
                      className="text-[#5f6d51]/60 p-0 h-auto text-xs"
                    >
                      Copy again
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => handleInvite(row)}
                    data-testid={`interest-invite-${row.id}`}
                    className="bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white"
                  >
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                    Invite
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
